export type AimPreferences = {
  aimResponse: 'steady' | 'direct';
  aimRelease: 'hold' | 'follow';
  stickDeadzone: number;
};
export const DEFAULT_AIM_PREFERENCES: AimPreferences = {
  aimResponse: 'steady', aimRelease: 'hold', stickDeadzone: 0.2,
};
export function aimPreferences(saved: Partial<AimPreferences>): AimPreferences {
  return {
    aimResponse: saved.aimResponse === 'direct' ? 'direct' : 'steady',
    aimRelease: saved.aimRelease === 'follow' ? 'follow' : 'hold',
    stickDeadzone: [0.12, 0.2, 0.3].includes(saved.stickDeadzone ?? -1) ? saved.stickDeadzone! : 0.2,
  };
}

/** Per-controller sampling history, outside the deterministic simulation. */
export type AimMemory = {
  x: number; y: number;
  direction?: { x: number; y: number };
  weak?: { x: number; y: number; since: number };
  active?: boolean;
  at?: number;
  deliberateAt?: number;
};

export function sampleStickAim(rx: number, ry: number, memory: AimMemory, prefs: AimPreferences, now: number) {
  rx = Number.isFinite(rx) ? rx : 0;
  ry = Number.isFinite(ry) ? ry : 0;
  const mag = Math.hypot(rx, ry), dt = Math.max(0, Math.min(50, now - (memory.at ?? now - 16.667)));
  memory.at = now;
  const idle = () => {
    memory.active = false;
    memory.x = memory.y = 0;
    return prefs.aimRelease === 'hold' && memory.direction ? { ...memory.direction } : { x: 0, y: 0 };
  };
  if (mag <= prefs.stickDeadzone) { memory.weak = undefined; return idle(); }
  const direction = { x: rx / mag, y: ry / mag };
  const old = memory.direction;
  const dot = old ? direction.x * old.x + direction.y * old.y : 1;
  if (mag < 0.5 && (!memory.active || dot < -0.5)) {
    // A weak reversal immediately after a flick is mechanical snap-back. A sustained gentle
    // nudge is intentional, including when it starts below the old half-stick threshold.
    if (dot < -0.5 && now - (memory.deliberateAt ?? -Infinity) < 100) {
      memory.weak = undefined;
      return idle();
    }
    if (!memory.weak || memory.weak.x * direction.x + memory.weak.y * direction.y < 0.9)
      memory.weak = { ...direction, since: now };
    if (now - memory.weak.since < 55) return idle();
  } else memory.weak = undefined;
  if (mag >= 0.5) memory.deliberateAt = now;
  let angle = Math.atan2(direction.y, direction.x);
  if (prefs.aimResponse === 'steady' && old && memory.active) {
    const previous = Math.atan2(old.y, old.x);
    const delta = Math.atan2(Math.sin(angle - previous), Math.cos(angle - previous));
    // Fine corrections settle over ~35 ms; a deliberate turn/flick responds immediately.
    if (Math.abs(delta) < 0.6) angle = previous + delta * (1 - Math.exp(-dt / 35));
  }
  memory.direction = { x: Math.cos(angle), y: Math.sin(angle) };
  memory.active = true;
  memory.x = memory.direction.x;
  memory.y = memory.direction.y;
  return { ...memory.direction };
}
